import { cloneDeep } from 'lodash/fp';

import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';
import { JsonSchema } from 'lib/json-schema';

/**
 * Takes any object and converts all custom code computed properties to base64 encoding to be compatible with components.
 *
 * @param moduleMetadata object
 */
export const base64EncodeCustomCode = (moduleMetadata: any) => {
  const nextModuleMetadata = cloneDeep(moduleMetadata);

  Object.keys(nextModuleMetadata).forEach((key) => {
    if (key === 'resolution-mechanism' && nextModuleMetadata[key]['serialized-code']) {
      nextModuleMetadata[key]['serialized-code'] = btoa(nextModuleMetadata[key]['serialized-code']);
    } else if (typeof nextModuleMetadata[key] === 'object') {
      nextModuleMetadata[key] = base64EncodeCustomCode(nextModuleMetadata[key]);
    } else if (Array.isArray(nextModuleMetadata[key])) {
      nextModuleMetadata[key] = nextModuleMetadata[key].map((item: any) =>
        base64EncodeCustomCode(item),
      );
    }
  });

  return nextModuleMetadata;
};

/**
 * Takes any object and converts all custom code computed properties from base64 encoding to string to be human readable.
 *
 * @param moduleMetadata object
 */
export const base64DecodeCustomCode = (moduleMetadata: any) => {
  const nextModuleMetadata = cloneDeep(moduleMetadata);

  Object.keys(nextModuleMetadata).forEach((key) => {
    if (key === 'resolution-mechanism' && nextModuleMetadata[key]['serialized-code']) {
      nextModuleMetadata[key]['serialized-code'] = atob(nextModuleMetadata[key]['serialized-code']);
    } else if (typeof nextModuleMetadata[key] === 'object') {
      nextModuleMetadata[key] = base64DecodeCustomCode(nextModuleMetadata[key]);
    } else if (Array.isArray(nextModuleMetadata[key])) {
      nextModuleMetadata[key] = nextModuleMetadata[key].map((item: any) =>
        base64DecodeCustomCode(item),
      );
    }
  });

  return nextModuleMetadata;
};

export const generateDefaultCodeSnippet = (
  returnType: ComputedProperty['return-type'],
  variables: Record<string, JsonSchema>,
) => `/**
* Compute Property
*
${Object.entries(variables)
  .map(
    ([name, schema]) =>
      `* @param {${'type' in schema && schema.type}} ${name} - ${schema.description}`,
  )
  .join('\n')}
*
* @returns {${returnType}} The value returned from function should match what is
* accepted by the computed property.
*/

function computeField(${Object.keys(variables).join(', ')}) {
  // YOUR CODE HERE
}
`;

/**
 * This function should eventually be refactored to parse the javascript code into an AST before extracting the argument.
 * The current regex implementation gets the job done, but is unable to handle edge cases.
 */
export function extractComputeFieldFunctionArgs(code: string) {
  const args = code.match(/function computeField\(([^)]+)\)/);
  if (!args) return [];

  return args[1].split(',').map((arg) => arg.trim());
}

// we append code to call and return the value of the function so users don't have to
export const generateRunAndReturnSnippet = (code: string) => {
  const extractedArgs = extractComputeFieldFunctionArgs(code);

  const runCodeSnippet = `${code}\nreturn computeField(${extractedArgs.join(', ')});`;

  return runCodeSnippet;
};
